import React, { useEffect, useRef } from "react";
import _ from "lodash";
import { Box, Button, Input, Select, SelectOption } from "@showitinc/showit-wc";

import AppConstants from "../../constants/AppConstants";
import UIActions from "../../actions/UIActions";
import AssetActions from "../../actions/AssetActions";
import AssetLibraryActions from "../../actions/AssetLibraryActions";
import AssetLibraryStore from "../../stores/AssetLibraryStore";
import useV2Feature from "../../hooks/useV2Feature";
import LoadingSpinner from "../LoadingSpinner";
import AssetDropContainer from "./AssetDropContainer";
import AssetLibrary from "./AssetLibrary";
import AssetLibraryFooter from "./AssetLibraryFooter";
import FolderChooser from "./FolderChooser";
import FolderNameDialog from "./FolderNameDialog";
import GalleryEditList from "./GalleryEditList";
import StockPhotoSearch from "./StockPhotoSearch/StockPhotoSearch";
import Modal from "../base/Modal";
import FormControl from "../base/FormControl";
import DialogOverlay from "../base/DialogOverlay";
import connectToStores from "../providers/connectToStores";
import uiPrefix from "../../utils/UiPrefixUtils";
import { createPexelsGalleryItem } from "../../utils/PexelsUtils";
import PexelsSelectHandler from "../utils/PexelsSelectHandler";
import styles from "./AssetLibrary.module.css";

const { ASSET_LIBRARY_TAB_FILES, ASSET_LIBRARY_TAB_STOCK } = AppConstants;

const uploadInputStyle = {
  opacity: "0",
  flex: "none",
  position: "absolute",
  left: "15px",
  height: "34px",
  width: "130px",
  overflow: "hidden",
};

const closeDialog = () => {
  AssetLibraryActions.updateState({ dialog: null });
};

const cancel = () => {
  UIActions.toggleAssetChooser(null);
};

/**
 * @param {Object} props
 * @param {number} props.numUploading
 */
const UploadNotification = ({ numUploading }) => (
  <div style={{ display: "flex", alignContent: "center", justifyContent: "center" }}>
    <div style={{ padding: 4 }}>
      <LoadingSpinner style={{ height: 24, width: 24 }} />
    </div>
    <div style={{ lineHeight: "2.5" }}>{`${numUploading} Upload(s) remaining`}</div>
  </div>
);

/**
 * @param {Object} props
 * @param {string} props.title
 * @param {string} props.actionLabel
 * @param {(data: Object) => void} props.onNameUpdate
 * @param {Object} [props.folder]
 */
const FolderNameOverlay = ({ title, actionLabel, onNameUpdate, folder }) => (
  <DialogOverlay onClick={closeDialog}>
    <FolderNameDialog
      title={title}
      actionLabel={actionLabel}
      onNameUpdate={onNameUpdate}
      label="Folder Name"
      onCancel={closeDialog}
      name={_.get(folder, "name", "")}
    />
  </DialogOverlay>
);

const onDeleteConfirmed = () => {
  AssetLibraryActions.deleteAssets();
};

const deleteFiles = () => {
  UIActions.modalOpen(AppConstants.MODAL_ACTION_CONFIRM, {
    title: "Delete Assets",
    question: "Are you sure you want to delete the selected assets?",
    message:
      "Any assets that are deleted but are used on one of your sites will still be visible for a little while",
    confirmBtn: "Delete",
    confirmAction: onDeleteConfirmed,
  });
};

/**
 * Media library modal (UIActions.toggleAssetChooser). The Stock Photos tab needs
 * `stockPhotoOnSelect` or gallery mode, where picks join the list instead of closing.
 * @param {Object} props
 * @param {Object} props.assetLibrary AssetLibraryStore state
 * @param {boolean} [props.demoMode]
 */
const AssetLibraryModal = (props) => {
  const { assetLibrary, demoMode } = props;
  const {
    title,
    activeTab,
    stockPhotoOnSelect,
    allowFilter,
    allowSort,
    allowSearch,
    allowDelete,
    allowMoveToFolder,
    allowUpload,
    allowFolderCreate,
    filter,
    sortBy,
    search,
    numUploads,
    numSelected,
    primaryActionBtnEnabled,
    cancelBtnEnabled,
    btnLabel,
    assets,
    uploading,
    folders,
    selectedFolder,
    galleryAssets,
    galleryItemsSelected,
    galleryEditEnabled,
    galleryExpanded,
  } = assetLibrary;

  // External image keys require the v2 renderer.
  const stockPhotosEnabled = useV2Feature();
  const showStockTab =
    stockPhotosEnabled && !demoMode && (!!stockPhotoOnSelect || galleryEditEnabled);
  // The expanded gallery organizer takes over the whole body, whichever tab is active.
  const currentTab =
    showStockTab && activeTab === ASSET_LIBRARY_TAB_STOCK && !galleryExpanded
      ? ASSET_LIBRARY_TAB_STOCK
      : ASSET_LIBRARY_TAB_FILES;

  // Stock photos added to the gallery list; they count as used only once saved.
  const pendingStockPhotosRef = useRef(new Map());

  useEffect(() => {
    if (!allowDelete || currentTab !== ASSET_LIBRARY_TAB_FILES) return undefined;

    const mousetrap = new Mousetrap();
    mousetrap.bind(["del", "backspace"], deleteFiles);
    return () => mousetrap.reset();
  }, [allowDelete, currentTab]);

  const addFiles = (e) => {
    e.preventDefault();
    e.stopPropagation();
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }

    const folder_id = !_.isEmpty(selectedFolder) ? selectedFolder.folder_id : null;

    AssetActions.uploadFiles({ files: Array.from(files), folder_id });
  };

  const onFilterChange = (e) => {
    const nextFilter = e.target.value ? parseInt(e.target.value) : false;

    AssetLibraryActions.updateFilter(nextFilter);
  };

  const onSortChange = (e) => {
    AssetLibraryActions.updateSort(e.target.value);
  };

  const onSearchChange = (e) => {
    AssetLibraryActions.updateSearch(e.target.value);
  };

  const recordSavedStockPhotos = (savedAssets) => {
    const savedKeys = new Set(savedAssets.map((item) => item.key));
    pendingStockPhotosRef.current.forEach((photo) => {
      if (savedKeys.has(photo.original)) {
        PexelsSelectHandler.recordUse(photo);
      }
    });
    pendingStockPhotosRef.current.clear();
  };

  const selectButtonClick = () => {
    // Actions run async; recording after the gallery write is queued keeps the
    // recents change out of that write's undo snapshot.
    AssetLibraryActions.acceptAssetSelection(
      galleryEditEnabled ? { onAccepted: recordSavedStockPhotos } : undefined,
    );
    UIActions.toggleAssetChooser(null);
  };

  const onStockPhotoSelect = (photo) => {
    stockPhotoOnSelect(photo);
    UIActions.toggleAssetChooser(null);
  };

  const addStockPhotoToGallery = (photo) => {
    pendingStockPhotosRef.current.set(photo.id, photo);
    AssetLibraryActions.addGalleryItems([createPexelsGalleryItem(photo)]);
  };

  const renderStockTab = () => {
    if (!galleryEditEnabled) {
      return <StockPhotoSearch onSelect={onStockPhotoSelect} onCancel={cancel} />;
    }

    return (
      <StockPhotoSearch
        onSelect={addStockPhotoToGallery}
        onCancel={cancel}
        confirmLabel="Add to Gallery"
        confirmVariant="outline"
        aside={({ selectedPhoto, confirmSelected }) => (
          <GalleryEditList
            galleryAssets={galleryAssets}
            galleryExpanded={galleryExpanded}
            galleryItemsSelected={galleryItemsSelected}
            numSelected={selectedPhoto ? 1 : 0}
            galleryEditEnabled={galleryEditEnabled}
            onAddToList={confirmSelected}
            addToListLabel="Add"
          />
        )}
        extraActions={
          <Button
            variant="feature"
            iconPrefix="add"
            onClick={selectButtonClick}
            data-testid="stock-gallery-save"
          >
            {btnLabel}
          </Button>
        }
      />
    );
  };

  const onAssetSelect = (selection) => {
    AssetLibraryActions.selectAsset(selection);
  };

  const onFolderDelete = (folder) => {
    const isEmpty = AssetLibraryStore.isFolderEmpty(folder);
    if (isEmpty) {
      AssetActions.deleteFolder(folder);
    } else {
      alert("Folder must be empty before it can deleted");
    }
  };

  const addNewFolder = (data) => {
    AssetActions.createFolder(data);
    closeDialog();
  };

  const updateFolder = (folder, data) => {
    AssetActions.updateFolder(folder, data);
    closeDialog();
  };

  const moveSelectedAssets = (folder) => {
    AssetLibraryActions.acceptMoveToFolder(folder);
    closeDialog();
  };

  const showFolderChooser = () => {
    AssetLibraryActions.showFolderChooser();
  };

  const renderTab = (tab, label) => (
    <div
      role="tab"
      aria-selected={currentTab === tab}
      className={`modal-header-tab ${currentTab === tab ? "active" : ""}`}
      tabIndex={0}
      onClick={() => AssetLibraryActions.selectTab(tab)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          AssetLibraryActions.selectTab(tab);
        }
      }}
      data-e2e={uiPrefix(`asset-library-${tab}-tab`)}
      data-testid={`asset-library-${tab}-tab`}
    >
      {label}
    </div>
  );

  const renderHeader = () => (
    <div className="modal-header-filters">
      <div className="modal-header-tabset" role="tablist">
        {renderTab(ASSET_LIBRARY_TAB_FILES, "Media Library")}
        {showStockTab && renderTab(ASSET_LIBRARY_TAB_STOCK, "Stock Photos")}
        {numUploads > 0 && <UploadNotification numUploading={numUploads} />}
      </div>
      {currentTab === ASSET_LIBRARY_TAB_FILES && (
        <div className={styles["modal-header-filter-fields"]}>
          {allowSearch ? (
            <Input
              label="Search:"
              placeholder="Enter filter"
              value={search}
              onSIChange={onSearchChange}
              className={styles["filter-fields-search"]}
            />
          ) : null}
          {allowFilter ? (
            <Select
              label="File types: "
              value={filter}
              onSISelect={onFilterChange}
              shift="left"
              className={styles["filter-fields-filetypes"]}
            >
              <SelectOption value=""> All </SelectOption>
              <SelectOption value={AppConstants.ASSET_TYPE_IMAGE}> Images </SelectOption>
              <SelectOption value={AppConstants.ASSET_TYPE_VIDEO}> Videos </SelectOption>
              <SelectOption value={AppConstants.ASSET_TYPE_FILE}> Files </SelectOption>
              <SelectOption value={AppConstants.ASSET_TYPE_LOTTIE}> Lottie </SelectOption>
            </Select>
          ) : null}
          {allowSort ? (
            <Select
              label="Sort by: "
              value={sortBy || "asset_name"}
              onSISelect={onSortChange}
              shift="left"
              className={styles["filter-fields-sortby"]}
            >
              <SelectOption value="asset_name">Name</SelectOption>
              <SelectOption value="ext">Extension</SelectOption>
            </Select>
          ) : null}
        </div>
      )}
    </div>
  );

  const renderDialog = () => {
    const dialogConfig = assetLibrary.dialog;
    if (!dialogConfig) return null;

    switch (dialogConfig.name) {
      case "folderRename": {
        const folder = dialogConfig.payload;
        return (
          <FolderNameOverlay
            title="Update Folder Name"
            actionLabel="Update"
            onNameUpdate={(data) => updateFolder(folder, data)}
            folder={folder}
          />
        );
      }
      case "folderCreate":
        return (
          <FolderNameOverlay title="Add New Folder" actionLabel="Add" onNameUpdate={addNewFolder} />
        );
      case "folderChooser":
        return (
          <DialogOverlay onClick={closeDialog}>
            <FolderChooser
              folders={folders}
              selected={selectedFolder}
              onCancel={closeDialog}
              onChoose={moveSelectedAssets}
            />
          </DialogOverlay>
        );
      default:
        return null;
    }
  };

  const renderActionButtons = () => {
    if (demoMode) {
      return null;
    }
    const hasSelected = numSelected > 0;

    return (
      <>
        {hasSelected && allowDelete ? (
          <Box mr="3">
            <Button variant="destructive" iconPrefix="trash" onClick={deleteFiles}>
              Delete selected
            </Button>
          </Box>
        ) : null}
        {hasSelected && allowMoveToFolder ? (
          <Box mr="3">
            <Button
              variant="outline"
              iconPrefix="openFolderFilled"
              onClick={showFolderChooser}
              data-testid="move-to-folder-button"
            >
              Move to folder
            </Button>
          </Box>
        ) : null}
      </>
    );
  };

  const renderPrimaryActionArea = () => {
    if (!primaryActionBtnEnabled) {
      return <div />;
    }

    const primaryActionAreaStyle = cancelBtnEnabled
      ? { width: 210, display: "flex", justifyContent: "space-between" }
      : {};

    return (
      <div style={primaryActionAreaStyle}>
        {cancelBtnEnabled ? (
          <Button variant="minimal" onClick={cancel}>
            Cancel
          </Button>
        ) : (
          <span />
        )}
        <Button variant="feature" iconPrefix="add" onClick={selectButtonClick}>
          {btnLabel}
        </Button>
      </div>
    );
  };

  const renderFooter = () => (
    <AssetLibraryFooter>
      {!demoMode && allowUpload ? (
        <Button
          variant="outline"
          data-e2e={uiPrefix("upload-files-btn")}
          className={uiPrefix("upload-files-btn")}
        >
          &#8673; Upload files
        </Button>
      ) : null}
      <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
        {renderActionButtons()}
        {renderPrimaryActionArea()}
      </div>
      {!demoMode ? (
        <div style={uploadInputStyle}>
          <FormControl
            type="file"
            multiple
            onChange={addFiles}
            groupClass="file-upload-hide"
            data-e2e={uiPrefix("file-upload-btn")}
          />
        </div>
      ) : null}
    </AssetLibraryFooter>
  );

  const modalProps = _.pick(props, Modal.BASIC_PROPS);

  return (
    <Modal {...modalProps} className="si-v-asset-library">
      <Modal.Dialog size="large">
        <Modal.Dialog.Header closeButton>
          <Modal.Dialog.Title>{title}</Modal.Dialog.Title>
          {renderHeader()}
        </Modal.Dialog.Header>
        {currentTab === ASSET_LIBRARY_TAB_STOCK ? (
          renderStockTab()
        ) : (
          <>
            <Modal.Dialog.Body style={{ padding: 0, display: "flex" }}>
              <AssetDropContainer selectedFolder={selectedFolder}>
                <AssetLibrary
                  assets={assets}
                  numSelected={numSelected}
                  uploading={uploading}
                  folders={folders}
                  selectedFolder={selectedFolder}
                  galleryEditEnabled={galleryEditEnabled}
                  galleryExpanded={galleryExpanded}
                  galleryAssets={galleryAssets}
                  galleryItemsSelected={galleryItemsSelected}
                  onAssetSelect={onAssetSelect}
                  onFolderDelete={onFolderDelete}
                  onAddFiles={addFiles}
                  folderCreateEnabled={allowFolderCreate}
                  allowFolderDrops={true}
                />
              </AssetDropContainer>
            </Modal.Dialog.Body>
            <Modal.Dialog.Footer>{renderFooter()}</Modal.Dialog.Footer>
          </>
        )}
        {renderDialog()}
      </Modal.Dialog>
    </Modal>
  );
};

export default connectToStores(AssetLibraryModal, [
  {
    store: AssetLibraryStore,
    state: { assetLibrary: AssetLibraryStore.getState },
  },
]);
